import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../utils/api';

const TEXTURAS = [
  { value: 'none',      label: 'Sin textura',  emoji: '⬜' },
  { value: 'particles', label: 'Partículas',   emoji: '✨' },
  { value: 'waves',     label: 'Ondas',        emoji: '🌊' },
  { value: 'geometric', label: 'Geométrico',   emoji: '🔷' },
];

const FUENTES = [
  { value: 'modern',  label: 'Moderna',   preview: 'Sora' },
  { value: 'elegant', label: 'Elegante',  preview: 'Playfair Display' },
  { value: 'tech',    label: 'Técnica',   preview: 'Share Tech Mono' },
  { value: 'rounded', label: 'Redondeada',preview: 'Nunito' },
];

const EditarPerfil = ({ adminMode = false }) => {
    const { uuid } = useParams();
    const navigate = useNavigate();
    const fotoRef  = useRef();

    const [perfil, setPerfil] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [uploadingFoto, setUploadingFoto] = useState(false);
    const [mensaje, setMensaje] = useState(null);
    const [error, setError]     = useState(null);
    const [tab, setTab]         = useState('datos'); // 'datos' | 'redes' | 'diseno'

    const apiSave = adminMode ? `/tarjetas/${uuid}/perfil` : `/perfil/${uuid}`;
    const backUrl  = adminMode ? '/tarjetas' : '/mis-tarjetas';

    useEffect(() => {
        api.get(`/perfil/${uuid}`)
            .then(({ data }) => setPerfil(data))
            .catch(() => navigate(backUrl))
            .finally(() => setLoading(false));
    }, [uuid, navigate, backUrl]);

    const handleChange = (e) => {
        setPerfil(prev => ({ ...prev, [e.target.name]: e.target.value }));
    };

    const handleSave = async () => {
        setSaving(true);
        setMensaje(null);
        setError(null);
        try {
            await api.put(apiSave, perfil);
            setMensaje('Perfil guardado correctamente ✓');
            setTimeout(() => setMensaje(null), 3000);
        } catch (e) {
            setError(e.response?.data?.error || 'Error guardando el perfil');
        } finally {
            setSaving(false);
        }
    };

    const handleFoto = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setUploadingFoto(true);
        const form = new FormData();
        form.append('foto', file);
        try {
            const { data } = await api.post(`/perfil/${uuid}/foto`, form, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            setPerfil(prev => ({ ...prev, foto_url: data.foto_url }));
        } catch (e) {
            setError(e.response?.data?.error || 'Error subiendo foto');
        } finally {
            setUploadingFoto(false);
        }
    };

    if (loading) return (
        <div className="flex items-center justify-center h-64">
            <div className="text-gray-400 text-sm">Cargando perfil...</div>
        </div>
    );

    const previewUrl = `${window.location.origin}/c/${perfil?.codigo}`;
    const acento = perfil?.color_acento || '#00d4ff';

    const inputCls = "w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300 bg-white";
    const labelCls = "block text-sm text-gray-600 mb-1 font-medium";

    return (
        <div className="max-w-2xl mx-auto">
            {/* Header */}
            <div className="mb-5 flex items-center gap-3">
                <button onClick={() => navigate(backUrl)} className="text-gray-400 hover:text-gray-600 text-xl">←</button>
                <div>
                    <h1 className="text-xl font-bold text-gray-800">
                        {adminMode ? 'Editar Perfil (Admin)' : 'Mi Tarjeta Digital'}
                    </h1>
                    <p className="text-xs text-gray-400 font-mono">{perfil?.codigo}</p>
                </div>
                <a href={previewUrl} target="_blank" rel="noreferrer"
                   className="ml-auto text-sm text-blue-500 hover:underline flex items-center gap-1">
                    Ver público →
                </a>
            </div>

            {mensaje && <div className="mb-4 p-3 bg-green-50 text-green-700 rounded-xl text-sm border border-green-200">{mensaje}</div>}
            {error   && <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-xl text-sm border border-red-200">{error}</div>}

            {/* Tabs */}
            <div className="flex gap-1 mb-5 bg-gray-100 rounded-xl p-1">
                {[
                    { id: 'datos',  label: '👤 Datos' },
                    { id: 'redes',  label: '🔗 Redes' },
                    { id: 'diseno', label: '🎨 Diseño' },
                ].map(t => (
                    <button key={t.id} onClick={() => setTab(t.id)}
                        className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all ${
                            tab === t.id ? 'bg-white shadow text-gray-800' : 'text-gray-500 hover:text-gray-700'
                        }`}>
                        {t.label}
                    </button>
                ))}
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">

                {/* ── Tab Datos ── */}
                {tab === 'datos' && (
                    <div className="space-y-5">
                        {/* Foto */}
                        <div className="flex items-center gap-5">
                            <div className="w-20 h-20 rounded-full overflow-hidden bg-gray-100 flex-shrink-0"
                                 style={{ border: `3px solid ${acento}` }}>
                                {perfil?.foto_url ? (
                                    <img src={perfil.foto_url} alt="Foto" className="w-full h-full object-cover" />
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center text-3xl text-gray-300">👤</div>
                                )}
                            </div>
                            <div>
                                <p className="text-sm font-medium text-gray-700 mb-1">Foto de perfil</p>
                                <p className="text-xs text-gray-400 mb-2">JPG, PNG o WEBP — máx 3MB</p>
                                <button onClick={() => fotoRef.current.click()} disabled={uploadingFoto}
                                    className="px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg text-gray-700 disabled:opacity-50">
                                    {uploadingFoto ? 'Subiendo...' : 'Cambiar foto'}
                                </button>
                                <input ref={fotoRef} type="file" accept="image/*" className="hidden" onChange={handleFoto} />
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div><label className={labelCls}>Nombre</label>
                                <input name="nombre" value={perfil?.nombre||''} onChange={handleChange} className={inputCls}/></div>
                            <div><label className={labelCls}>Apellido</label>
                                <input name="apellido" value={perfil?.apellido||''} onChange={handleChange} className={inputCls}/></div>
                            <div><label className={labelCls}>Cargo</label>
                                <input name="cargo" value={perfil?.cargo||''} onChange={handleChange} className={inputCls}/></div>
                            <div><label className={labelCls}>Empresa</label>
                                <input name="empresa" value={perfil?.empresa||''} onChange={handleChange} className={inputCls}/></div>
                            <div><label className={labelCls}>📞 Teléfono</label>
                                <input name="telefono" value={perfil?.telefono||''} onChange={handleChange} className={inputCls}/></div>
                            <div><label className={labelCls}>💬 WhatsApp</label>
                                <input name="whatsapp" value={perfil?.whatsapp||''} onChange={handleChange} placeholder="573001234567" className={inputCls}/></div>
                            <div><label className={labelCls}>✉️ Email</label>
                                <input name="email" type="email" value={perfil?.email||''} onChange={handleChange} className={inputCls}/></div>
                            <div><label className={labelCls}>🌐 Sitio web</label>
                                <input name="website" value={perfil?.website||''} onChange={handleChange} placeholder="https://miweb.com" className={inputCls}/></div>
                        </div>

                        <div>
                            <label className={labelCls}>Biografía profesional</label>
                            <textarea name="bio" value={perfil?.bio||''} onChange={handleChange} rows={3}
                                placeholder="Cuéntale a la gente quién eres..."
                                className={`${inputCls} resize-none`}/>
                        </div>
                    </div>
                )}

                {/* ── Tab Redes ── */}
                {tab === 'redes' && (
                    <div className="space-y-4">
                        <p className="text-xs text-gray-400 mb-2">Agrega las URLs completas de tus redes. Las que estén vacías no se mostrarán.</p>
                        {[
                            { name: 'linkedin',  label: 'LinkedIn',  placeholder: 'https://linkedin.com/in/usuario',  color: '#0A66C2' },
                            { name: 'instagram', label: 'Instagram', placeholder: 'https://instagram.com/usuario',    color: '#E1306C' },
                            { name: 'tiktok',    label: 'TikTok',    placeholder: 'https://tiktok.com/@usuario',      color: '#010101' },
                            { name: 'youtube',   label: 'YouTube',   placeholder: 'https://youtube.com/@canal',       color: '#FF0000' },
                        ].map(r => (
                            <div key={r.name}>
                                <label className={labelCls} style={{ color: r.color }}>
                                    {r.label}
                                </label>
                                <input name={r.name} value={perfil?.[r.name]||''} onChange={handleChange}
                                    placeholder={r.placeholder} className={inputCls}/>
                            </div>
                        ))}
                    </div>
                )}

                {/* ── Tab Diseño ── */}
                {tab === 'diseno' && (
                    <div className="space-y-6">
                        {/* Colores */}
                        <div>
                            <p className="text-sm font-semibold text-gray-700 mb-3">Colores</p>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className={labelCls}>Color de fondo</label>
                                    <div className="flex items-center gap-2">
                                        <input type="color" name="color_fondo"
                                            value={perfil?.color_fondo||'#0a0f1e'}
                                            onChange={handleChange}
                                            className="w-10 h-10 rounded-lg border border-gray-200 cursor-pointer p-0.5"/>
                                        <input name="color_fondo" value={perfil?.color_fondo||'#0a0f1e'}
                                            onChange={handleChange} className={`${inputCls} flex-1`}
                                            placeholder="#0a0f1e"/>
                                    </div>
                                </div>
                                <div>
                                    <label className={labelCls}>Color de acento</label>
                                    <div className="flex items-center gap-2">
                                        <input type="color" name="color_acento"
                                            value={perfil?.color_acento||'#00d4ff'}
                                            onChange={handleChange}
                                            className="w-10 h-10 rounded-lg border border-gray-200 cursor-pointer p-0.5"/>
                                        <input name="color_acento" value={perfil?.color_acento||'#00d4ff'}
                                            onChange={handleChange} className={`${inputCls} flex-1`}
                                            placeholder="#00d4ff"/>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Preview mini */}
                        <div className="rounded-xl p-4 flex items-center gap-3"
                             style={{ background: perfil?.color_fondo||'#0a0f1e' }}>
                            <div className="w-10 h-10 rounded-full flex-shrink-0"
                                 style={{ border: `2px solid ${acento}`, background: `${acento}33` }}/>
                            <div>
                                <div className="text-sm font-bold text-white">Vista previa</div>
                                <div className="text-xs" style={{ color: acento }}>Color de acento</div>
                            </div>
                            <div className="ml-auto">
                                <div className="px-3 py-1 rounded-lg text-xs font-medium text-black"
                                     style={{ background: acento }}>Botón</div>
                            </div>
                        </div>

                        {/* Textura */}
                        <div>
                            <p className="text-sm font-semibold text-gray-700 mb-3">Textura de fondo</p>
                            <div className="grid grid-cols-2 gap-2">
                                {TEXTURAS.map(t => (
                                    <button key={t.value} onClick={() => setPerfil(p => ({...p, textura: t.value}))}
                                        className={`flex items-center gap-2 p-3 rounded-xl border text-sm transition-all ${
                                            perfil?.textura === t.value
                                                ? 'border-blue-400 bg-blue-50 text-blue-700 font-medium'
                                                : 'border-gray-200 text-gray-600 hover:border-gray-300'
                                        }`}>
                                        <span>{t.emoji}</span> {t.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Fuente */}
                        <div>
                            <p className="text-sm font-semibold text-gray-700 mb-3">Fuente</p>
                            <div className="grid grid-cols-2 gap-2">
                                {FUENTES.map(f => (
                                    <button key={f.value} onClick={() => setPerfil(p => ({...p, fuente: f.value}))}
                                        className={`flex flex-col p-3 rounded-xl border text-left transition-all ${
                                            perfil?.fuente === f.value
                                                ? 'border-blue-400 bg-blue-50'
                                                : 'border-gray-200 hover:border-gray-300'
                                        }`}>
                                        <span className="text-xs text-gray-500 mb-0.5">{f.label}</span>
                                        <span className="text-sm text-gray-800" style={{ fontFamily: f.preview }}>
                                            Hola Mundo
                                        </span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {/* Guardar */}
                <div className="flex justify-between items-center pt-5 mt-5 border-t border-gray-100">
                    <a href={previewUrl} target="_blank" rel="noreferrer"
                       className="text-sm text-blue-500 hover:underline">
                        Ver perfil público →
                    </a>
                    <button onClick={handleSave} disabled={saving}
                        className="px-6 py-2.5 rounded-xl text-white font-medium text-sm disabled:opacity-50 transition"
                        style={{ backgroundColor: 'var(--color-primary)' }}>
                        {saving ? 'Guardando...' : 'Guardar cambios'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default EditarPerfil;
